import { gql, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { SaveJourneySettingsResponse } from 'modules/journey/components/SettingsTakeover/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { getWorkflow, updateWorkflow } from '../ducks';
import { BackendJourneyConversion } from '../types';

const SUCCESS_MESSAGE = 'Conversion settings updated!';
const FAILURE_MESSAGE = `We’re having trouble updating your conversion settings right now. Please try again later.`;

export const saveConversionsMutation = gql`
  mutation addWorkflowGoal(
    $workflowId: Long!
    $workflowType: WorkflowType!
    $conversions: [CustomConversionInput!]!
  ) {
    addWorkflowGoal(
      workflowId: $workflowId
      workflowType: $workflowType
      conversions: $conversions
    ) {
      id
      isActive
      createdAt
      updatedAt
      conversions {
        eventName
        attributionMode
        attributionPeriodHours
      }
    }
  }
`;

type useSaveConversionsProps = {
  conversions: any[];
  /** Callback when the update was successful */
  onSuccess?: () => void;
  /** Should a success message show? */
  showSuccessToast?: boolean;
  /** Should the workflow be refetched after a successful update? */
  fetchWorkflowOnSuccess?: boolean;
};

type AddConversionsResponse = {
  addWorkflowGoal: BackendJourneyConversion;
};

export const useAddJourneyConversions = () => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const [saveConversions] = useMutation<AddConversionsResponse>(saveConversionsMutation);
  const { id: workflowId, workflowType } = workflow || {};
  return async ({
    conversions,
    onSuccess,
    showSuccessToast = true,
  }: useSaveConversionsProps): Promise<SaveJourneySettingsResponse> => {
    try {
      const { data } = await saveConversions({
        variables: {
          workflowId,
          workflowType,
          conversions,
        },
      });
      const isJourneyConversionsActive = data?.addWorkflowGoal?.isActive;

      if (isJourneyConversionsActive) {
        dispatch(
          updateWorkflow(
            { goals: [data?.addWorkflowGoal] },
            { suppressHasUnsavedChangesUpdate: true },
          ),
        );
        if (onSuccess) onSuccess();
        if (showSuccessToast) dispatch(addPositiveToast(SUCCESS_MESSAGE));
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useAddJourneyConversions'));
      }
      return { hasErrors: !isJourneyConversionsActive };
    } catch {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useAddJourneyConversions'));
      return { hasErrors: true };
    }
  };
};
